import React from 'react';
import { useSurveyStore } from '@/stores/survey/store';
import { Check } from 'lucide-react';

interface StepIndicatorProps {
    stepTitles: string[];
    currentStep: number;
    onPrevious: () => void;
    progressPercentage: number;
    selectedWizard?: string | undefined;
    hasExtraStep?: boolean;
    isLastStep?: boolean;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
    stepTitles,
    currentStep,
    hasExtraStep,
    isLastStep,
}) => {
    const { registrationStep } = useSurveyStore();

    // Map currentStep + registrationStep to active step index dynamically
    const hasExtra = hasExtraStep ?? false;
    let activeStep = 1;
    const registrationStepIndex = hasExtra ? 1 : 0;

    if (isLastStep) {
        activeStep = stepTitles.length + 3; // Exceeds steps.length to mark all completed
    } else if (currentStep === registrationStepIndex) {
        if (registrationStep === 1) activeStep = 1;
        else activeStep = 2; // OTP or Name/Email
    } else if (currentStep > registrationStepIndex) {
        activeStep = Math.min(currentStep - registrationStepIndex + 2, stepTitles.length + 2);
    }

    // Do not show the indicator on the very first screen (Initial State) or on the Phone input step
    if (currentStep === 0 || activeStep === 1) {
        return null;
    }

    // Build steps dynamically using server-provided stepTitles for step 3 onwards
    const steps = [
        { id: 1, label: '۱. ثبت شماره' },
        { id: 2, label: '۲. تایید موبایل' },
        ...stepTitles.map((title, idx) => ({
            id: idx + 3,
            label: `${idx + 3}. ${title}`,
        })),
    ];

    return (
        <div
            dir='rtl'
            className='w-full flex flex-col items-center px-6 pt-10 pb-4 border-b border-gray-100 dark:border-white/10 select-none'
        >
            {/* Steps Container */}
            <div className='w-full max-w-[420px] flex flex-col'>
                {/* Circles & Line Row */}
                <div className='relative w-full flex items-center justify-between h-8'>
                    {/* Progress Lines Container */}
                    <div className='absolute top-1/2 -translate-y-1/2 left-10 right-10 h-[2px] bg-gray-200 dark:bg-white/10 -z-10'>
                        {/* Active Progress Line */}
                        <div
                            className='absolute top-0 bottom-0 right-0 bg-[#EEAC43] transition-all duration-300'
                            style={{
                                width: `${Math.min(((activeStep - 1) / (steps.length - 1)) * 100, 100)}%`,
                            }}
                        />
                    </div>

                    {steps.map((step) => {
                        const isCompleted = step.id < activeStep;
                        const isActive = step.id === activeStep;

                        return (
                            <div
                                key={step.id}
                                className='relative z-10 w-20  flex justify-center items-center'
                            >
                                <div
                                    className={`size-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                                        isCompleted
                                            ? 'bg-[#e7c282] dark:bg-[#b48336] text-white border-2 border-[#e7c282] dark:border-[#b48336]'
                                            : isActive
                                              ? 'bg-white dark:bg-[#1a1a1a] text-[#EEAC43] border-2 border-[#EEAC43] shadow-md shadow-[#EEAC43]/20'
                                              : 'bg-white dark:bg-[#1a1a1a] text-gray-400 dark:text-white/30 border-2 border-gray-200 dark:border-white/10'
                                    }`}
                                >
                                    {isCompleted ? (
                                        <Check className='size-4 stroke-[3px]' />
                                    ) : (
                                        step.id
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Labels Row */}
                <div className='w-full flex justify-between items-start mt-2'>
                    {steps.map((step) => {
                        const isCompleted = step.id < activeStep;
                        const isActive = step.id === activeStep;

                        return (
                            <div
                                key={step.id}
                                className='w-20 text-center flex flex-col justify-start'
                            >
                                <span
                                    className={`text-[9px] md:text-[10px] font-medium leading-tight transition-all duration-300 ${
                                        isCompleted || isActive
                                            ? 'text-gray-900 dark:text-white font-bold'
                                            : 'text-gray-400 dark:text-white/20'
                                    }`}
                                >
                                    {step.label}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default StepIndicator;
